import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'
import rootStore from '../../../store/RootStore'
import { fetchSnapshotWindow } from '../../../services/api/reportAPI'

const pad = n => String(n).padStart(2, '0')

// Format a Date as a local <input type="datetime-local"> value ("YYYY-MM-DDTHH:mm").
const toLocalInput = d =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`

/**
 * Create-report entry for PABS: a snapshot pinned to a chosen survey year and as-of date.
 * The allowed as-of range [minAsOf, now] is fetched from the backend (history start), so the
 * date picker is bounded and there is no hardcoded floor on the client. The chosen instant is
 * sent as a UTC ISO string and replayed via FOR SYSTEM_TIME AS OF; on success it opens the report.
 */
const CreateSnapshotDialog = observer(({ isOpen, onClose }) => {
  const {
    reportStore: { saveSnapshot },
    productsStore: { currentProduct },
    planStore: { currentPlan },
  } = rootStore
  const navigate = useNavigate()
  const toast = useToast()
  const initialFocusRef = useRef(null)

  const [name, setName] = useState('')
  const [dataYear, setDataYear] = useState(new Date().getFullYear())
  const [asOfLocal, setAsOfLocal] = useState('')
  const [asOfWindow, setAsOfWindow] = useState(null) // { minDate, maxDate } as Date objects
  const [saving, setSaving] = useState(false)

  // Load the allowed as-of window whenever the dialog opens.
  useEffect(() => {
    if (!isOpen || currentProduct !== 'pa' || !currentPlan?.planId) return
    let cancelled = false
    ;(async () => {
      try {
        const w = await fetchSnapshotWindow({
          productId: currentProduct,
          planId: currentPlan.planId,
        })
        const minDate = w?.minAsOf ? new Date(w.minAsOf) : null
        const maxDate = w?.maxAsOf ? new Date(w.maxAsOf) : new Date()
        if (cancelled) return
        setAsOfWindow({ minDate, maxDate })
        setAsOfLocal(toLocalInput(maxDate)) // default to "now"
        if (w?.dataYear) setDataYear(w.dataYear) // default to the configured survey cycle
      } catch (e) {
        if (cancelled) return
        const maxDate = new Date()
        setAsOfWindow({ minDate: null, maxDate })
        setAsOfLocal(toLocalInput(maxDate))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isOpen, currentProduct, currentPlan?.planId])

  const reset = useCallback(() => {
    setName('')
    setDataYear(new Date().getFullYear())
    setAsOfLocal('')
    setAsOfWindow(null)
  }, [])

  const handleClose = useCallback(() => {
    if (saving) return
    reset()
    onClose()
  }, [saving, reset, onClose])

  const handleCreate = useCallback(async () => {
    if (!name.trim() || !asOfLocal || !dataYear) return

    const asOfDate = new Date(asOfLocal)
    if (isNaN(asOfDate.getTime())) {
      toast({ title: 'Invalid as-of date', status: 'error', duration: 3000, isClosable: true })
      return
    }
    if (asOfWindow?.minDate && asOfDate < asOfWindow.minDate) {
      toast({
        title: `The as-of date cannot be earlier than ${asOfWindow.minDate.toLocaleDateString()}`,
        status: 'error',
        duration: 3500,
        isClosable: true,
      })
      return
    }
    // TEMP bypass: future as-of dates are allowed for now (clock-skew workaround).

    setSaving(true)
    try {
      const snapshotId = await saveSnapshot({
        name: name.trim(),
        asOf: asOfDate.toISOString(), // local -> UTC
        dataYear: Number(dataYear),
      })
      if (typeof snapshotId !== 'string' || !snapshotId.trim())
        throw new Error('No snapshot id returned')

      toast({ title: 'Snapshot created', status: 'success', duration: 2500, isClosable: true })
      reset()
      onClose()
      navigate(`/reports/LiveReport?snapshot=${snapshotId}`)
    } catch (e) {
      toast({
        title: 'Failed to create snapshot',
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    } finally {
      setSaving(false)
    }
  }, [name, asOfLocal, dataYear, asOfWindow, saveSnapshot, toast, reset, onClose, navigate])

  const minLocal = asOfWindow?.minDate ? toLocalInput(asOfWindow.minDate) : undefined
  const rangeHint =
    asOfWindow?.minDate && asOfWindow?.maxDate
      ? `Allowed: ${asOfWindow.minDate.toLocaleDateString()} – ${asOfWindow.maxDate.toLocaleDateString()}.`
      : 'The report will show the data as it stood at this moment.'

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      initialFocusRef={initialFocusRef}
      isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create snapshot report</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack
            spacing="16px"
            align="stretch">
            <FormControl isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                ref={initialFocusRef}
                value={name}
                placeholder="e.g. Board pack — Q2"
                onChange={e => setName(e.target.value)}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Survey year</FormLabel>
              <NumberInput
                value={dataYear}
                min={2000}
                max={new Date().getFullYear() + 1}
                onChange={(_, v) => setDataYear(Number.isNaN(v) ? '' : v)}>
                <NumberInputField />
              </NumberInput>
              <FormHelperText>Which survey cycle to freeze.</FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Data as of</FormLabel>
              <Input
                type="datetime-local"
                value={asOfLocal}
                min={minLocal}
                onChange={e => setAsOfLocal(e.target.value)}
              />
              <FormHelperText>{rangeHint}</FormHelperText>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={handleClose}
            isDisabled={saving}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleCreate}
            isLoading={saving}
            isDisabled={!name.trim() || !asOfLocal || !dataYear}>
            Create
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
})

CreateSnapshotDialog.displayName = 'CreateSnapshotDialog'

export default CreateSnapshotDialog
